// frontend/src/pages/portal/modifiers/NestedGroupPicker.js
//
// Modal opened from inside ModifierGroupEditor when a tenant clicks
// "＋ Add a nested choice to {option}". Lists every candidate group; a group
// that already contains a nested option of its own is greyed out with the
// depth-guard reason instead of just being missing from the list.
//
// This greying is a courtesy UI hint only — Task 3's backend
// (validate_attachment / MAX_MODIFIER_DEPTH) re-checks ATTACHMENT rules
// (nesting depth / cycles) server-side and is the real enforcement for
// those. Note this is scoped to attachment only: SELECTION rules (min/max/
// required counts on what a cashier actually picks) are a separate concern
// handled by modifierTree.js client-side and are NOT currently re-validated
// server-side at transaction time — see modifier_validation.py's module
// docstring. The `tooDeep` flag on each candidate is computed by the caller
// (the editor, which has the full group + add-on catalog needed to work it
// out) and just rendered here.
import React, { useEffect } from 'react';
import { C, FONT } from '../../../styles/theme';
import { ruleLabel } from './modifierRules';

export default function NestedGroupPicker({ open, groups = [], optionName, onPick, onClose, t }) {
    useEffect(() => {
        const onKey = e => { if (e.key === 'Escape') onClose && onClose(); };
        if (open) window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    return (
        <div
            style={{
                position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.42)', zIndex: 1100,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onClick={onClose}
        >
            <div
                className="portal-modal-card"
                style={{
                    background: C.white, borderRadius: 15, width: 'min(480px, calc(100vw - 40px))',
                    maxHeight: 'calc(100vh - 70px)', display: 'flex', flexDirection: 'column',
                    overflow: 'hidden', fontFamily: FONT, boxShadow: '0 24px 70px rgba(0,0,0,0.24)',
                }}
                onClick={e => e.stopPropagation()}
            >
                <div style={{
                    padding: '19px 24px 15px', borderBottom: `1px solid ${C.border}`,
                    display: 'flex', alignItems: 'center', gap: 12,
                }}>
                    <h3 style={{ fontSize: 17, fontWeight: 800, flex: 1, color: C.text, margin: 0 }}>
                        {t('nested_choice_for', { name: optionName }, `Nested choice for "${optionName}"`)}
                    </h3>
                    <button
                        onClick={onClose}
                        aria-label={t('close', {}, 'Close')}
                        style={{
                            width: 32, height: 32, borderRadius: 9, background: C.bg, border: `1px solid ${C.border}`,
                            color: C.muted, display: 'grid', placeItems: 'center', fontSize: 16, cursor: 'pointer',
                        }}
                    >✕</button>
                </div>

                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '20px 24px' }}>
                    <p style={{ fontSize: 13, color: C.muted, fontWeight: 600, marginBottom: 16 }}>
                        {t('pick_group_for_option', { name: optionName }, `Pick the group that should open when a cashier selects ${optionName}.`)}
                    </p>

                    {groups.length === 0 ? (
                        <div style={{ fontSize: 13, color: C.mutedLight, textAlign: 'center', padding: '20px 0' }}>
                            {t('no_candidate_groups', {}, 'No other groups yet — create one first.')}
                        </div>
                    ) : groups.map(g => (
                        <button
                            key={g.group_id}
                            disabled={g.tooDeep}
                            onClick={() => !g.tooDeep && onPick && onPick(g)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 11, padding: '11px 13px',
                                border: `1.5px solid ${C.border}`, borderRadius: 10, marginBottom: 7,
                                fontSize: 13, fontWeight: 700, width: '100%', textAlign: 'left',
                                background: g.tooDeep ? C.bg : C.white, color: g.tooDeep ? C.mutedLight : C.text,
                                cursor: g.tooDeep ? 'not-allowed' : 'pointer', opacity: g.tooDeep ? 0.6 : 1,
                            }}
                        >
                            <span>{g.name}</span>
                            <span style={{
                                marginLeft: 'auto', fontSize: 10.5, fontWeight: 700,
                                color: g.tooDeep ? C.red : C.mutedLight,
                            }}>
                                {g.tooDeep
                                    ? t('would_nest_too_deep', { max: 2 }, 'Would nest too deep (max 2 levels)')
                                    : ruleLabel(g, t)}
                            </span>
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}
